import { Chip, Stack, Tooltip, Typography } from "@mui/material";
import { cyan, grey, purple } from "@mui/material/colors";
import { contactReportRoleToLabel } from "encamp-shared/src/utils/contactReportRole";
import { ContactReportRole } from "generated-graphql/graphql";

export type ContactRoleProgramArea = "EPCRA" | "RCRA" | "GENERAL";

export function getContactRoleProgramArea(
  role: ContactReportRole | string
): ContactRoleProgramArea {
  const upper = role.toString().toUpperCase();
  if (upper.startsWith("EPCRA")) return "EPCRA";
  if (upper.startsWith("RCRA")) return "RCRA";
  return "GENERAL";
}

const PROGRAM_AREA_STYLES: Record<
  ContactRoleProgramArea,
  { backgroundColor: string; color: string }
> = {
  EPCRA: {
    backgroundColor: purple[50],
    color: purple[700],
  },
  RCRA: {
    backgroundColor: cyan[50],
    color: cyan[800],
  },
  GENERAL: {
    backgroundColor: `${grey[300]}55`,
    color: grey[800],
  },
};

function getChipLabel(role: ContactReportRole | string): string {
  const programArea = getContactRoleProgramArea(role);
  const fullLabel = contactReportRoleToLabel(role);
  if (programArea === "RCRA" && fullLabel.startsWith("RCRA ")) {
    return fullLabel.slice("RCRA ".length);
  }
  return fullLabel;
}

export function ContactRoleChip({ role }: { role: ContactReportRole | string }) {
  const programArea = getContactRoleProgramArea(role);
  const styles = PROGRAM_AREA_STYLES[programArea];
  const label = getChipLabel(role);
  const fullLabel = contactReportRoleToLabel(role);
  const showTooltip = label !== fullLabel;

  const chip = (
    <Chip
      label={label}
      size="small"
      variant="filled"
      sx={{
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        height: 22,
      }}
    />
  );

  if (showTooltip) {
    return (
      <Tooltip title={fullLabel} placement="top">
        {chip}
      </Tooltip>
    );
  }
  return chip;
}

export function ContactRoleChipList({
  roles,
}: {
  roles: Array<ContactReportRole | string> | null | undefined;
}) {
  if (!roles || roles.length === 0) return null;
  const unique = Array.from(new Set(roles.map((r) => r.toString())));
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ py: 0.5 }}>
      {unique.map((role) => (
        <ContactRoleChip key={role} role={role} />
      ))}
    </Stack>
  );
}

function LegendChip({ programArea }: { programArea: ContactRoleProgramArea }) {
  const styles = PROGRAM_AREA_STYLES[programArea];
  return (
    <Chip
      label="-"
      size="small"
      variant="filled"
      sx={{
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        height: 22,
        minWidth: 32,
      }}
    />
  );
}

export function ContactRoleChipLegend() {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={2}
      alignItems="center"
      sx={{ mt: 1.5 }}
      aria-label="Contact role legend"
    >
      <Stack direction="row" alignItems="center" gap={0.75}>
        <LegendChip programArea="EPCRA" />
        <Typography variant="caption" color="text.secondary">
          Chemicals Program Role
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <LegendChip programArea="RCRA" />
        <Typography variant="caption" color="text.secondary">
          Waste Program Role
        </Typography>
      </Stack>
    </Stack>
  );
}
